import { Feature, Plan, ProjectTemplate, UserLayer } from '@core/api';
import { AppConfig } from '@core/config';
import { SelectedGeoJSONFeature, SelectedRenderFeature } from '@shared/open-layers';

import { GeoJSONFeature } from '../models/feature.model';

export function toGeoJSONFeature({
  id,
  geometry,
  properties,
  createdAt,
  updatedAt,
  propertyId,
  syncedAt,
  geometrySyncedAt,
}: Feature): GeoJSONFeature {
  return {
    id,
    type: 'Feature',
    geometry,
    properties: { ...properties, createdAt, updatedAt },
    propertyId,
    createdAt,
    updatedAt,
    syncedAt,
    geometrySyncedAt,
  };
}

export function isActiveFeature(feature: GeoJSONFeature | GeoJSON.Feature): boolean {
  return !(feature as GeoJSONFeature).deleted;
}

export function getActiveFeatures(features: (GeoJSONFeature | GeoJSON.Feature)[] | undefined): GeoJSONFeature[] {
  return ((features || []) as GeoJSONFeature[]).filter(isActiveFeature);
}

export function sortFeaturesByOrder<T extends { id?: string | number }>(features: T[], order: (string | number)[] | undefined): T[] {
  if (!features.length || !order?.length) {
    return features;
  }

  const indexById = new Map(order.map((id, index) => [String(id), index]));

  return [...features].sort((a, b) => {
    const aIndex = a.id === undefined ? undefined : indexById.get(String(a.id));
    const bIndex = b.id === undefined ? undefined : indexById.get(String(b.id));

    if (aIndex == null && bIndex == null) {
      return 0;
    }

    if (aIndex == null) {
      return 1;
    }

    if (bIndex == null) {
      return -1;
    }

    return aIndex - bIndex;
  });
}

export function convertUserLayerToPlan(layer: UserLayer, features: GeoJSON.Feature[] = []): Plan {
  return {
    ...layer,
    geojson: {
      type: 'FeatureCollection',
      features: features ? features : [],
    },
  };
}

export function isRenderFeatureSelected(feature: SelectedGeoJSONFeature | SelectedRenderFeature): feature is SelectedRenderFeature {
  return feature.type === 'RenderFeature';
}

export function isPlanReadonly(plan: Plan, templates: ProjectTemplate[], config: AppConfig) {
  const template = templates?.find((t) => t.id === plan?.subtype);

  return !plan || plan.readonly || !template || (config.featureFlags['lockSurveys'] && template.type === 'survey');
}
